import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, useAuth } from "@/lib/auth-context";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const valid = password.length >= 8 && password === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid || !token) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      // Backend sets the auth cookies; refresh user state and bounce home.
      await refreshUser();
      toast.success("Password updated. You're logged in.");
      setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FBF9F4] flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
          <p className="font-heading text-lg font-semibold text-[#0A192F]">
            Missing reset token
          </p>
          <Link to="/forgot-password" className="text-sm text-[#C5A059] underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#FBF9F4] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        {done ? (
          <div className="space-y-5 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF8E7] border-2 border-[#C5A059] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#C5A059]" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A192F]">
              Password updated
            </h1>
            <p className="text-sm text-slate-600">Redirecting…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-[#0A192F]">
              Set a new password
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Pick something at least 8 characters. You'll be logged in automatically.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="rp-pw"
                  className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1.5"
                >
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="rp-pw"
                    data-testid="reset-password-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 rounded-lg border-slate-300"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="rp-confirm"
                  className="block text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] mb-1.5"
                >
                  Confirm password
                </label>
                <Input
                  id="rp-confirm"
                  data-testid="reset-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11 rounded-lg border-slate-300"
                />
                {confirm && password !== confirm && (
                  <p className="mt-1 text-xs text-red-600">Passwords don't match.</p>
                )}
              </div>
              <Button
                type="submit"
                data-testid="reset-submit"
                disabled={!valid || submitting}
                className="w-full h-11 rounded-lg bg-[#0A192F] hover:bg-[#112A46] text-white font-semibold disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Update password"
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
